import { i18n_t as $t } from '@/utils/i18n';
import keyBy from 'lodash/keyBy';
import Vue from 'vue';
import {
    createEmailDomain,
    createOmnichannel,
    deleteEmailDomain,
    deleteOmniChannelById,
    getEmailProfilesForKind,
    loadEmail,
    loadEmailIdentities,
    loadEmailIdentity,
    loadEmailProviders,
    loadEmails,
    loadOrganizationEmailIdentities,
    loadOrganizationEmailIdentity,
    replaceDefaultSubject,
    saveEmailProfile,
    updateEmailField,
    updateEmailFieldObj,
    updateEmailMailFromDomain,
    updateOmniChannelField,
} from './emails.utils';

import { LOADING_STATES } from '@/constants';
import { EMAIL_STATES } from '@/emails/constants';
import { cloneAssociatedGuide, isIntegratedEmail, isScheduled } from '@/emails/helpers';
import { PROVIDER_TYPES } from '@/orchestrate/journeys/integrations.constants';
import { DOMAIN_STATUSES } from '@/settings/subscription-settings/domains-settings/utils/constants';
import { BuildingBlock, BuildingBlockProperties } from '@pendo/services/BuildingBlocks';
import { EMAIL } from '@pendo/services/Constants';
import { getEmailType } from '@pendo/services/EmailUtils';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { canCreateEmail } from './emails-permissions.utils';
const { EMAIL_TYPES } = EMAIL;
const { LOADING, RESOLVED, REJECTED, EMPTY } = LOADING_STATES;
const { AWS } = PROVIDER_TYPES;
const { VERIFIED, UNVERIFIED, PENDING } = DOMAIN_STATUSES;
const EMAIL_CHANNEL = 'email';

export const EMPTY_VALUE = '__EMPTY__';

export const FIELD_TYPES = {
    EMAIL_SETTINGS: 'emailsettings',
    SUBJECT: 'subject',
};

export const emailObj = {
    name: undefined,
    appId: undefined,
    audience: [
        {
            source: {
                visitors: null,
            },
        },
        {
            filter: 'false',
        },
    ],
    audienceUiHint: {
        filters: [
            {
                kind: 'visitor',
                segmentId: 'noOne',
            },
        ],
    },
    channelType: undefined,
    subject: undefined,
    guideId: undefined,
    emailSettings: {
        friendlyFromName: undefined,
        from: undefined,
        identity: undefined,
        recipientMetadataPropertyName: undefined,
        replyTo: undefined,
        type: undefined,
    },
};

const emails = {
    namespaced: true,
    state: {
        map: {},
        integration: {},
        integrationError: {},
        activeEmailId: null,
        domains: null,
        editing: false,
        saving: false,
        getAllEmailsStatus: LOADING,
        profiles: [],
        promises: {
            loadProfiles: null,
        },
        identitiesStatus: null,
    },
    mutations: {
        addOrUpdateEmail (state, { email }) {
            state.map = {
                ...state.map,
                [email.id]: replaceDefaultSubject(email),
            };
        },
        setActiveEmailById (state, { id }) {
            state.activeEmailId = id;
        },
        setDomains (state, { domains }) {
            state.domains = domains;
        },
        addDomain (state, { domain }) {
            if (!state.domains) state.domains = [];

            state.domains.push(domain);
        },
        removeDomain (state, { identity }) {
            state.domains = state.domains.filter((domain) => domain.identity !== identity);
        },
        setMap (state, { map }) {
            state.map = map;
        },
        setIntegration (state, { provider, list }) {
            Vue.set(state.integration, provider, list);
        },
        setIntegrationError (state, { provider, error }) {
            state.integrationError[provider] = error;
        },
        removeEmail (state, { id }) {
            Vue.delete(state.map, id);
        },
        setSaving (state, { saving }) {
            state.saving = saving;
        },
        setEditing (state, { editing }) {
            state.editing = editing;
        },
        getAllEmailsStatus (state, { status }) {
            state.getAllEmailsStatus = status;
        },
        setProfiles (state, { profiles }) {
            state.profiles = profiles;
        },
        setPromise (state, { promise, key }) {
            Vue.set(state.promises, key, promise);
        },
        setIdentitiesStatus (state, { status }) {
            state.identitiesStatus = status;
        },
    },
    actions: {
        async loadAll ({ commit, state, rootGetters }, { reFetch = false } = {}) {
            const hasOrchestrateProductLine = rootGetters['productLines/hasProductLinePurchased']('orchestrate');
            if (!hasOrchestrateProductLine || ([RESOLVED, EMPTY].includes(state.getAllEmailsStatus) && !reFetch)) {
                return;
            }

            try {
                commit('setMap', { map: {} });
                commit('getAllEmailsStatus', { status: LOADING });
                const { data } = await loadEmails();
                const emails = data.map((email) => replaceDefaultSubject(email));
                const getAllEmailsStatus = emails.length ? RESOLVED : EMPTY;
                commit('setMap', { map: keyBy(emails, 'id') });
                commit('getAllEmailsStatus', { status: getAllEmailsStatus });
            } catch {
                commit('getAllEmailsStatus', { status: REJECTED });
            }
        },
        async loadOne ({ getters, commit, dispatch }, { id, setActive = false, reFetch = false }) {
            const emailInState = getters.getEmailById(id);

            if (emailInState && !reFetch) {
                if (setActive) dispatch('setActiveEmail', emailInState);

                return;
            }

            try {
                const { data: email } = await loadEmail(id);
                commit('addOrUpdateEmail', { email });

                if (setActive) dispatch('setActiveEmail', email);
            } catch {
                throw new Error($t('emails.Failed to load email'));
            }
        },
        async loadEmailProviders ({ commit }) {
            try {
                const { data: providers } = await loadEmailProviders();

                return providers;
            } catch {
                commit('setIntegrationError', {
                    provider: 'general',
                    error: new Error($t('emails.Failed to load email providers')),
                });
            }
        },
        async updateEmailField ({ commit, state }, { id = state.activeEmailId, field, value }) {
            const email = state.map[id];

            if (!email) return;

            try {
                const { data: updatedEmail } = await updateEmailField(id, EMAIL_CHANNEL, field, value);

                commit('addOrUpdateEmail', {
                    email: updatedEmail,
                });
            } catch {
                throw new Error($t('emails.Failed to update email field'));
            }
        },
        async updateEmailFieldObj ({ commit, state }, { id = state.activeEmailId, field, value }) {
            const email = state.map[id];

            if (!email) return;

            try {
                const { data: updatedEmail } = await updateEmailFieldObj(id, EMAIL_CHANNEL, field, value);

                commit('addOrUpdateEmail', {
                    email: updatedEmail,
                });
            } catch {
                throw new Error($t('emails.Failed to update email field'));
            }
        },
        async updateOmniChannelField ({ commit, state }, { emailId, field, value }) {
            commit('setSaving', { saving: true });
            const email = state.map[emailId || state.activeEmailId];

            if (!email) return;

            try {
                const { data: updatedEmail } = await updateOmniChannelField(email.id, field, value);

                commit('addOrUpdateEmail', {
                    email: updatedEmail,
                });
            } catch {
                throw new Error($t('emails.Unable to update email'));
            } finally {
                commit('setSaving', { saving: false });
            }
        },
        async createOmnichannel (
            { commit },
            {
                name,
                description,
                appId,
                channelType,
                subject = EMPTY_VALUE,
                guideId,
                provider = AWS,
                setActive = false,
                schedule,
                attributes = {},
                emailType,
            },
        ) {
            try {
                const { data: email } = await createOmnichannel({
                    ...emailObj,
                    name,
                    description,
                    appId,
                    channelType,
                    subject,
                    guideId,
                    provider,
                    schedule,
                    attributes,
                    emailType,
                });

                commit('addOrUpdateEmail', { email });

                if (setActive) commit('setActiveEmailById', { id: email.id });

                return email;
            } catch {
                throw new Error($t('emails.Failed to create omnichannel'));
            }
        },
        async createEmailDomain ({ rootGetters, dispatch }, { domain, mailFrom }) {
            try {
                const { data: createdDomain } = await createEmailDomain(domain);

                await updateEmailMailFromDomain(domain, mailFrom);

                // Update the emailSettings in the subscription with the new domain
                const activeSub = rootGetters['subscription/getActiveSub'];
                const { emailSettings } = activeSub;
                await dispatch(
                    'subscription/updateProperty',
                    {
                        property: 'emailSettings',
                        value: {
                            ...(emailSettings || {}),
                            emailIdentities: {
                                ...(emailSettings.emailIdentities || {}),
                                [createdDomain.identity]: {
                                    type: createdDomain.type,
                                    identity: createdDomain.identity,
                                    mailFromDomain: mailFrom,
                                },
                            },
                        },
                    },
                    { root: true },
                );

                await dispatch('loadDomainIdentities');
                await dispatch('loadDomainRecords');

                return createdDomain;
            } catch (ex) {
                throw new Error($t('emails.Create email domain was unsuccessful', { error: ex }));
            }
        },
        async deleteEmailDomain ({ rootGetters, dispatch, commit }, { identity }) {
            try {
                await deleteEmailDomain(identity);

                const activeSub = rootGetters['subscription/getActiveSub'];
                const { emailSettings } = activeSub;
                const emailIdentities = { ...(emailSettings.emailIdentities || {}) };
                delete emailIdentities[identity];

                await dispatch(
                    'subscription/updateProperty',
                    {
                        property: 'emailSettings',
                        value: {
                            ...(emailSettings || {}),
                            emailIdentities,
                        },
                    },
                    { root: true },
                );

                commit('removeDomain', { identity });
            } catch (ex) {
                throw new Error($t('emails.Delete email domain was unsuccessful', { error: ex }));
            }
        },
        setActiveEmail ({ commit, dispatch }, { id, guideId }) {
            commit('setActiveEmailById', { id });
            dispatch('guides/loadOne', { setActive: true, id: guideId }, { root: true });
        },
        resetActiveEmail ({ commit }) {
            commit('setActiveEmailById', { id: null });
            commit('guides/setActiveGuide', { guide: null }, { root: true });
        },
        async deleteEmail ({ commit }, { id }) {
            try {
                await deleteOmniChannelById(id);

                commit('removeEmail', { id });
            } catch {
                throw new Error($t('emails.Failed to delete email'));
            }
        },
        async updateEmailSettings ({ dispatch, getters }, { settings }) {
            const { subjectLine, previewText, ...detailsSettings } = settings;

            if (detailsSettings.replyTo === '') {
                detailsSettings.replyTo = detailsSettings.from;
            }

            try {
                await dispatch('updateEmailFieldObj', {
                    field: FIELD_TYPES.EMAIL_SETTINGS,
                    value: detailsSettings,
                });

                await dispatch('updateEmailField', {
                    field: FIELD_TYPES.SUBJECT,
                    value: subjectLine,
                });

                if (!!previewText || previewText === '') {
                    const clonedGuide = cloneDeep(getters.getAssociatedGuide);
                    clonedGuide.steps[0].buildingBlocks = JSON.parse(clonedGuide.steps[0].buildingBlocks);
                    const previewTextBlock = BuildingBlock.findBlockByDomId(
                        clonedGuide.steps[0].buildingBlocks,
                        'pendo-preview-text-',
                    );
                    BuildingBlockProperties.updateOrCreateProperty(previewTextBlock, 'text', 'string', previewText);

                    await dispatch(
                        'guides/saveGuide',
                        {
                            guide: clonedGuide,
                        },
                        { root: true },
                    );
                }
            } catch {
                throw new Error($t('emails.Failed to update email settings'));
            }
        },
        async cloneGuideByEmailId ({ getters, rootGetters, dispatch }, { id }) {
            const email = getters.getEmailById(id);
            if (!email) return;

            const { guideId, name: emailName } = email;
            const guide = rootGetters['guides/getGuideById'](guideId);
            if (!guide) return;

            try {
                const app = rootGetters['apps/getAppById'](guide.appId);
                const clonedAssociatedGuide = await cloneAssociatedGuide(guide, app, emailName);

                const clonedGuide = await dispatch(
                    'guides/saveGuide',
                    { guide: clonedAssociatedGuide },
                    { root: true },
                );

                return clonedGuide;
            } catch {
                throw new Error($t('emails.Failed to clone guide by email id'));
            }
        },
        async cloneEmail ({ dispatch }, { id }) {
            const clonedGuide = await dispatch('cloneGuideByEmailId', { id });

            try {
                const clonedEmail = await dispatch('createOmnichannel', {
                    name: clonedGuide.name,
                    appId: clonedGuide.appId,
                    channelType: EMAIL_CHANNEL,
                    guideId: clonedGuide.id,
                    emailType: getEmailType(clonedGuide),
                });

                return clonedEmail;
            } catch {
                throw new Error($t('emails.Failed to clone email'));
            }
        },
        async updateEmailName ({ state, dispatch }, { name }) {
            const email = state.activeEmailId && state.map[state.activeEmailId];

            if (!email) return;
            const { guideId } = email;

            await dispatch('updateOmniChannelField', { field: 'name', value: name });
            await dispatch('guides/updateGuideName', { id: guideId, name }, { root: true });
        },
        async loadDomainIdentities ({ commit, rootGetters }, { appId } = {}) {
            const orgId = rootGetters['organization/getActive']?.id;

            const promises = [loadEmailIdentities(appId), ...(orgId ? [loadOrganizationEmailIdentities(orgId)] : [])];

            const results = await Promise.all(promises);
            const domains = results.reduce((acc, { data = [] }) => [...acc, ...data], []);

            commit('setDomains', { domains });
        },
        async loadDomainRecords ({ state, commit, rootGetters }) {
            if (!state.domains) return;

            const orgId = rootGetters['organization/getActive']?.id;

            const loadDomainRecord = async ({ identity, isOrganization }) => {
                const { data } =
                    isOrganization && orgId
                        ? await loadOrganizationEmailIdentity(orgId, identity)
                        : await loadEmailIdentity(identity);

                return data;
            };

            const domains = await Promise.all(
                state.domains.filter((domain) => domain?.type === 'DOMAIN').map(loadDomainRecord),
            );

            commit('setDomains', { domains });
        },
        async loadDomainIdentitiesAndRecords ({ commit, rootGetters, dispatch, state }, { appId } = {}) {
            commit('setIdentitiesStatus', { status: LOADING });

            try {
                // order matters: loadDomainIdentities -> loadDomainRecords
                await dispatch('loadDomainIdentities', { commit, rootGetters }, { appId });
                await dispatch('loadDomainRecords', { commit, state, rootGetters });

                if (state.domains.length === 0) {
                    commit('setIdentitiesStatus', { status: EMPTY });
                } else {
                    commit('setIdentitiesStatus', { status: RESOLVED });
                }
            } catch {
                commit('setIdentitiesStatus', { status: REJECTED });
            }
        },
        loadProfiles ({ commit, state }, { reFetch = false, kind } = {}) {
            if (state.promises.loadProfiles) {
                return state.promises.loadProfiles;
            }

            if (!isEmpty(state.profiles) && !reFetch) {
                return Promise.resolve();
            }

            const promise = getEmailProfilesForKind(kind)
                .then((profiles) => {
                    commit('setProfiles', { profiles });
                })
                .catch((error) => {
                    throw error;
                })
                .finally(() => {
                    commit('setPromise', { key: 'loadProfiles', promise: null });
                });

            commit('setPromise', { key: 'loadProfiles', promise });

            return promise;
        },
        async saveProfile (
            { dispatch },
            { friendlyFromName, identity, from, replyTo, recipientMetadataPropertyName, isDefault, kind, type },
        ) {
            try {
                await saveEmailProfile({
                    friendlyFromName,
                    identity,
                    from,
                    replyTo,
                    recipientMetadataPropertyName: recipientMetadataPropertyName || 'agent.email',
                    type: type || 'DOMAIN',
                    isDefault,
                    kind,
                });
                await dispatch('loadProfiles', { reFetch: true, kind });
            } catch (error) {
                throw error;
            }
        },
    },
    getters: {
        getSaving: (state) => state.saving,
        getAllEmailsStatus: (state) => state.getAllEmailsStatus,
        list: (state) => Object.values(state.map),
        getValidDomainIdentities: (state, { getDomains }) => {
            return getDomains?.map((domain) => domain.identity) || [];
        },
        getEmailById: (state, { getValidDomainIdentities }) => (id) => {
            const email = state.map[id];
            if (!email) return null;

            const emailIdentity = email.emailSettings?.identity;

            if (emailIdentity && !getValidDomainIdentities.includes(emailIdentity)) {
                return {
                    ...email,
                    emailSettings: {
                        ...email.emailSettings,
                        identity: '',
                    },
                };
            }

            return email;
        },
        getEmailTypeById: (state, { getEmailById }) => (id) => getEmailById(id)?.emailType || EMAIL_TYPES.MARKETING,
        getSubjectLine: (state, { getActiveEmail }) => replaceDefaultSubject(getActiveEmail)?.subject,
        getActiveEmail: (state, { getEmailById }) => getEmailById(state.activeEmailId),
        getEmailSettings: (state, { getActiveEmail }) => getActiveEmail?.emailSettings || {},
        getActiveEmailAnalytics: (state) => state.activeEmailAnalytics,
        getActiveEmailAnalyticsStatus: (state, { getActiveEmailAnalytics }) => getActiveEmailAnalytics.status,
        isEmailLaunched: (state, { getActiveEmail, isEmailScheduled }) =>
            [EMAIL_STATES.SENT, EMAIL_STATES.DISABLED].includes(getActiveEmail?.status) ||
            (getActiveEmail?.status === EMAIL_STATES.PUBLIC && !isEmailScheduled),
        isEmailScheduled: (state, { getActiveEmail }) => isScheduled(getActiveEmail),
        isEmailSettingsEmpty: (state, { getEmailSettings }) => !getEmailSettings || !getEmailSettings.identity,
        isEditing: (state) => state.editing,
        isEmailEditingBlocked: (state, { getActiveEmail }) =>
            !canCreateEmail({ appId: getActiveEmail.appId }) ||
            [EMAIL_STATES.SENT, EMAIL_STATES.PUBLIC, EMAIL_STATES.DISABLED].includes(getActiveEmail?.status),
        isEmailIntegrated: (state, { getActiveEmail }) => !!(getActiveEmail && isIntegratedEmail(getActiveEmail)),
        hasEmailStandAlone: (state, getters, rootState, rootGetters) => {
            return rootGetters['productLines/hasProductLinePurchased']('orchestrate', 'emailStandAloneEnabled');
        },
        getDomains: (state) => state.domains || [],
        excludeMailFromVerification: (state, getters, rootState, rootGetters) => {
            return rootGetters['auth/hasSegmentFlag']('excludeMailFromVerification');
        },
        getVerifiedDomains (state, { getDomains, excludeMailFromVerification }) {
            return getDomains?.filter(({ identityVerified = false, mailFromDomainVerified = false }) => {
                if (excludeMailFromVerification) {
                    return identityVerified;
                }

                return identityVerified && mailFromDomainVerified;
            });
        },
        getVerifiedDomainIdentities: (state, { getVerifiedDomains }) => {
            return getVerifiedDomains?.map(({ identity }) => identity) || [];
        },
        getSendingDisabled: (state, { getVerifiedDomains }) => {
            return getVerifiedDomains?.filter(({ sendingEnabled = false }) => !sendingEnabled) || [];
        },
        getVerifiedDomainForSending: (state, { getVerifiedDomains }) => {
            return (
                getVerifiedDomains
                    ?.filter(({ sendingEnabled = false }) => sendingEnabled)
                    .map(({ identity }) => identity) || []
            );
        },
        domainVerificationStatus: (state, { getVerifiedDomainIdentities, excludeMailFromVerification }) => (
            domain = {},
        ) => {
            if (getVerifiedDomainIdentities.includes(domain.identity)) return VERIFIED;

            const { dkimVerificationStatus, mailFromDomainStatus } = domain;

            if (excludeMailFromVerification) {
                return (
                    {
                        FAILED: UNVERIFIED,
                        PENDING,
                    }[dkimVerificationStatus] || PENDING
                );
            }

            if ([dkimVerificationStatus, mailFromDomainStatus].includes('FAILED')) {
                return UNVERIFIED;
            }

            return PENDING;
        },
        getProfileById: (state) => (id) => {
            return state.profiles.find((profile) => profile.id === id);
        },
        defaultProfile: (state) => {
            return state.profiles.find((profile) => profile.default);
        },
        getEmailQuotaSize: (state, getters, rootState, rootGetters) => {
            return rootGetters['auth/hasSegmentFlag']('allow250kEmailQuota') ? 250000 : 60000;
        },
        getAssociatedGuide: (state, getters, rootState, rootGetters) => {
            const activeEmail = getters.getActiveEmail;
            if (!activeEmail) return null;

            const guide = rootGetters['guides/getGuideById'](activeEmail.guideId);
            if (!guide) return null;

            return guide;
        },
        getBuildingBlocks: (state, { getAssociatedGuide }) => {
            const bbString = get(getAssociatedGuide, 'steps.0.buildingBlocks');
            if (!bbString) return null;

            return JSON.parse(bbString);
        },
        isLocalizationEnabled: (state, getters, rootState, rootGetters) => {
            const hasApplicationFlag = rootGetters['apps/getApplicationFlag'](
                getters.getAssociatedGuide?.appId,
                'guideLocalization',
            );
            const hasProductLine = rootGetters['productLines/hasProductLinePurchased'](
                'orchestrate',
                'emailLocalization',
            );
            const hasSegmentFlag = rootGetters['auth/hasSegmentFlag']('emailLocalization');

            return hasSegmentFlag || (hasApplicationFlag && hasProductLine);
        },
        getIdentitiesStatus: (state) => state.identitiesStatus,
        hasEmailThrottlingPermission: (state, getters, rootState, rootGetters) => {
            const hasSegmentFlag = rootGetters['auth/hasSegmentFlag']('emailThrottling');
            const isAdmin = rootGetters['auth/isAdmin'];
            const hasSubscriptionFlag = rootGetters['subscription/getFeatureFlag']('EmailThrottleSettings.enabled');

            return hasSegmentFlag && isAdmin && hasSubscriptionFlag;
        },
        getEmailsTabs: (state, { hasEmailThrottlingPermission }) => {
            const tabs = [
                { prop: 'overview', label: $t('emails.Overview'), lazy: true },
                { prop: 'metrics', label: $t('emails.Metrics'), lazy: true },
            ];

            if (hasEmailThrottlingPermission) {
                tabs.push({ prop: 'settings', label: $t('emails.Settings'), lazy: true });
            }

            return tabs;
        },
        isMarketingEmail: (state, getters) => (id) => {
            return getters.getEmailTypeById(id) === EMAIL_TYPES.MARKETING;
        },
    },
};

export default emails;
